export const eur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export const dateFr = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

export const DEVIS_STATUS = {
  brouillon: { label: "Brouillon", cls: "bg-slate-200 text-slate-700" },
  envoye: { label: "Envoyé", cls: "bg-blue-100 text-blue-700" },
  accepte: { label: "Accepté", cls: "bg-green-100 text-green-700" },
  refuse: { label: "Refusé", cls: "bg-red-100 text-red-700" },
};

export const FACTURE_STATUS = {
  brouillon: { label: "Brouillon", cls: "bg-slate-200 text-slate-700" },
  emise: { label: "Émise", cls: "bg-blue-100 text-blue-700" },
  envoyee: { label: "Envoyée", cls: "bg-indigo-100 text-indigo-700" },
  partiellement_payee: { label: "Partiellement payée", cls: "bg-amber-100 text-amber-700" },
  payee: { label: "Payée", cls: "bg-green-100 text-green-700" },
  annulee_par_avoir: { label: "Annulée par avoir", cls: "bg-red-100 text-red-700" },
  // rétro-compat anciens statuts
  non_payee: { label: "Émise", cls: "bg-blue-100 text-blue-700" },
};

// Affichage TVA : ne jamais montrer "0 %" comme un taux, afficher "TVA non applicable"
export const tvaLabel = (rate) => (Number(rate) === 0 ? "TVA non applicable" : `${Number(rate)}%`);

export const PRICING_MODES = {
  fixe: { label: "Prix fixe", short: "" },
  inclus: { label: "Inclus", short: "Inclus" },
  a_partir_de: { label: "À partir de", short: "à partir de" },
  minimum: { label: "Minimum", short: "min." },
  sur_devis: { label: "Sur devis", short: "Sur devis" },
};
export const priceModeShort = (m) => PRICING_MODES[m]?.short || "";

export const CHANTIER_TYPES = [
  "Installation climatisation",
  "Pompe à chaleur",
  "Dépannage",
  "Entretien / maintenance",
  "Mise en service",
  "Travaux de plomberie",
  "Autre",
];

export const PRESTATION_TYPES = [
  "Fourniture matériel",
  "Main d'œuvre",
  "Déplacement",
  "Mise en service",
  "Accessoires",
];

export const TVA_RATES = [
  { value: 0, label: "0%" },
  { value: 5.5, label: "5,5%" },
  { value: 10, label: "10%" },
  { value: 20, label: "20%" },
];

export const LIBRARY_ITEM_TYPES = [
  "matériel",
  "main-d'œuvre",
  "fourniture",
  "forfait",
  "déplacement",
  "autre",
];

export const UNITS = ["U", "ml", "m²", "m³", "h", "heure", "j", "forfait", "kg", "L"];

// Catégories proposées lors de la mémorisation d'une ligne libre (cohérentes avec la bibliothèque)
export const MEMORIZE_CATEGORIES = [
  "Climatisation / Froid",
  "Pompe à chaleur",
  "Chauffage / CVC",
  "Plomberie",
  "Services",
];

const CATEGORY_KEYWORDS = [
  ["Pompe à chaleur", ["pompe a chaleur", "pompe à chaleur", "pac ", "pac air"]],
  ["Plomberie", ["plomberie", "sanitaire", "wc", "lavabo", "douche", "robinet", "evacuation", "évacuation", "pvc", "per ", "multicouche", "fuite eau"]],
  ["Chauffage / CVC", ["chaudiere", "chaudière", "radiateur", "vmc", "ventilation", "chauffage", "hydraulique"]],
  ["Climatisation / Froid", ["clim", "monosplit", "bi-split", "bisplit", "split", "cassette", "console", "gainable", "frigo", "frigorifique", "fluide", "azote", "tirage", "condensat", "goulotte", "liaison"]],
  ["Services", ["main", "oeuvre", "œuvre", "deplacement", "déplacement", "mise en service", "diagnostic", "etude", "étude", "maintenance", "depannage", "dépannage", "intervention", "pose", "installation"]],
];

// Devine une catégorie à partir de la désignation (retourne une clé de MEMORIZE_CATEGORIES)
export function guessCategory(designation) {
  const d = (designation || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  for (const [cat, kws] of CATEGORY_KEYWORDS) {
    if (kws.some((k) => d.includes(k.normalize("NFD").replace(/[\u0300-\u036f]/g, "")))) return cat;
  }
  return "Services";
}

// Prestations préremplies (raccourcis)
export const MATERIAL_TYPES = [
  "Split mural",
  "Multi-split",
  "Gainable",
  "Cassette",
  "Console",
  "Pompe à chaleur air/air",
  "Pompe à chaleur air/eau",
  "Chauffe-eau thermodynamique",
];

export const ACCESSOIRES = [
  "Goulotte",
  "Support mural",
  "Pompe de relevage",
  "Liaisons cuivre",
  "Câble d'alimentation",
  "Console de sol",
];

export const PRESET_PRESTATIONS = [
  { designation: "Pose climatiseur monosplit", prestation_type: "Main d'œuvre", unit_price_ht: 450, tva_rate: 20 },
  { designation: "Pose bi-split", prestation_type: "Main d'œuvre", unit_price_ht: 750, tva_rate: 20 },
  { designation: "Installation pompe à chaleur air/air", prestation_type: "Main d'œuvre", unit_price_ht: 1200, tva_rate: 10 },
  { designation: "Mise en service circuit frigorifique", prestation_type: "Mise en service", unit_price_ht: 180, tva_rate: 20 },
  { designation: "Tirage de ligne cuivre", prestation_type: "Accessoires", unit_price_ht: 35, tva_rate: 20 },
  { designation: "Pose support mural extérieur", prestation_type: "Accessoires", unit_price_ht: 60, tva_rate: 20 },
  { designation: "Installation pompe de relevage condensats", prestation_type: "Accessoires", unit_price_ht: 120, tva_rate: 20 },
  { designation: "Dépannage climatisation", prestation_type: "Main d'œuvre", unit_price_ht: 90, tva_rate: 20 },
];
